import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'ebc_cookie_consent';

/**
 * Small unobtrusive Cookie Consent banner.
 * - Only appears when the visitor has never made a choice.
 * - "Accept" or "Reject" both dismiss it and are stored in localStorage,
 *   so it never nags a returning visitor.
 * - Bilingual (AR / EN), respects RTL, links to /privacy-policy.
 */
const CookieConsent = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay a tick so it doesn't flash before hydration / language detection.
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        const timer = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {
      /* localStorage unavailable — leave hidden */
    }
  }, []);

  const dismiss = (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:left-4 md:left-auto md:right-6 md:bottom-6 z-[90]
                 sm:max-w-md md:max-w-lg animate-fade-in-up"
      role="dialog"
      aria-live="polite"
      aria-label={isAr ? 'إشعار ملفات تعريف الارتباط' : 'Cookie notice'}
    >
      <div
        className="relative rounded-2xl overflow-hidden backdrop-blur-xl
                   bg-white/95 dark:bg-slate-900/95
                   border border-slate-200 dark:border-slate-800
                   shadow-[0_20px_60px_-15px_rgba(2,117,60,0.35)]"
      >
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-blue via-brand-green to-brand-orange" />

        <button
          type="button"
          onClick={() => dismiss('dismissed')}
          data-testid="cookie-consent-close"
          aria-label={isAr ? 'إغلاق' : 'Close'}
          className={`absolute top-2.5 ${isAr ? 'left-2.5' : 'right-2.5'} w-7 h-7 rounded-lg
                      bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300
                      hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors
                      flex items-center justify-center`}
        >
          <X className="w-4 h-4" />
        </button>

        <div className={`p-5 sm:p-6 flex gap-4 ${isAr ? 'flex-row-reverse text-right' : 'text-left'}`}>
          <div className="flex-shrink-0">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-blue/15 to-brand-green/15
                            border border-brand-blue/25 dark:border-brand-blue/30
                            flex items-center justify-center">
              <Cookie className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm md:text-base font-bold text-slate-900 dark:text-white mb-1">
              {isAr ? 'نستخدم ملفات تعريف الارتباط' : 'We use cookies'}
            </p>
            <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {isAr ? (
                <>
                  نستخدم ملفات تعريف الارتباط لتحسين تجربتك على موقع شركة التوازن البيئي.
                  للمزيد من التفاصيل، يُرجى مراجعة{' '}
                  <Link
                    to="/privacy-policy"
                    data-testid="cookie-consent-privacy-link"
                    className="font-semibold text-brand-blue-700 dark:text-brand-blue-300 hover:underline"
                  >
                    سياسة الخصوصية
                  </Link>
                  .
                </>
              ) : (
                <>
                  We use cookies to enhance your experience on the Environmental Balance Company
                  website. For more details, please see our{' '}
                  <Link
                    to="/privacy-policy"
                    data-testid="cookie-consent-privacy-link"
                    className="font-semibold text-brand-blue-700 dark:text-brand-blue-300 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </>
              )}
            </p>

            <div className={`mt-4 flex flex-wrap items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                onClick={() => dismiss('accepted')}
                data-testid="cookie-consent-accept"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl
                           bg-gradient-to-r from-brand-blue to-brand-blue-700
                           hover:from-brand-blue-700 hover:to-brand-blue-800
                           text-white text-xs md:text-sm font-semibold shadow-md
                           hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                {isAr ? 'موافق' : 'Accept'}
              </button>
              <button
                type="button"
                onClick={() => dismiss('rejected')}
                data-testid="cookie-consent-reject"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl
                           bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200
                           hover:bg-slate-200 dark:hover:bg-slate-700
                           text-xs md:text-sm font-semibold transition-colors"
              >
                {isAr ? 'رفض' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
